<!--
  Vue file for the app component.
  @author Adrian Rebmann, Yannik Sprengart, Sebastian Grill, Steffen Schuhmann
-->

<template>
  <v-app>
    <div id="app">
      <navigation />
      <div id="main-body">
        <transition
          :name="transitionName"
          :mode="transitionMode"
          :enter-active-class="transitionEnterActiveClass"
          @beforeLeave="beforeLeave"
          @enter="enter"
          @afterEnter="afterEnter"
          >
          <router-view class="pt-6" />
        </transition>
      </div>
      <Notification />
      <WernerFooter />
    </div>
  </v-app>
</template>

<script>

import Navigation from "./components/app/navigation/Navigation.vue";
import WernerFooter from "./components/app/footer/WernerFooter.vue";
import Notification from "./components/app/notification/Notification.vue";

const DEFAULT_TRANSITION = "";
const DEFAULT_TRANSITION_MODE = "out-in";

export default {
  name: "App",
  /**
   * Components to be made available to the Vue instance.
   */
  components: { Navigation, WernerFooter, Notification },

  /**
   * The vue instance's data object.
   * @property {string} transitionName - The name of the transition.
   * @property {string} transitionMode - The mode of the transition.
   * @property {string} transitionEnterActiveClass - Class for the enter transition.
   */
  data() {
    return {
      transitionName: DEFAULT_TRANSITION,
      transitionMode: DEFAULT_TRANSITION_MODE,
      transitionEnterActiveClass: "",
    };
  },

  /**
   * Called after the instance has been created.
   */
  created() {
    this.$router.beforeEach((to, from, next) => {
      let transitionName =
        to.meta.transitionName ||
        from.meta.transitionName ||
        DEFAULT_TRANSITION;

      //Slide transition
      if (transitionName === "slide") {
        const toDepth = to.path.split("/").length;
        const fromDepth = from.path.split("/").length;
        transitionName = toDepth < fromDepth ? "slide-right" : "slide-left";
      }

      this.transitionMode = DEFAULT_TRANSITION_MODE;
      this.transitionEnterActiveClass = `${transitionName}-enter-active`;

      //Zoom transitions
      if (to.meta.transitionName === "zoom") {
        this.transitionMode = "in-out";
        this.transitionEnterActiveClass = "zoom-enter-active";
        // Disable scrolling in the background.
        //document.body.style.overflow = "hidden";
      }

      //Disable transition effect when the page component already had a transition
      if (from.meta.transitionName === "zoom") {
        this.transitionMode = null;
        this.transitionEnterActiveClass = null;
      }

      this.transitionName = transitionName;

      next();
    });
  },

  /**
   * The vue instance's method declaration.
   */
  methods: {
    /**
     * Saves the current pages page before leaving the page.
     * @param {*} element - The element of the current page.
     */
    beforeLeave(element) {
      this.prevHeight = getComputedStyle(element).height;
    },

    /**
     * Triggers the transition of the height of the new page, to which it is being transitioned.
     * @param {*} element - The element of the current page.
     */
    enter(element) {
      const { height } = getComputedStyle(element);
      // eslint-disable-next-line no-param-reassign
      element.style.height = this.prevHeight;
      setTimeout(() => {
        // eslint-disable-next-line no-param-reassign
        element.style.height = height;
      });
    },

    /**
     * Sets the height of the page back to auto to make sure its height is dynamic again.
     * @param {*} element - The element of the current page.
     */
    afterEnter(element) {
      // eslint-disable-next-line no-param-reassign
      element.style.height = `auto`;
    },
  },
};
</script>
<style lang="scss">
@import "~vue-material/dist/theme/engine"; // Import the theme engine
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

html,
body {
  font-family: "Inter", sans-serif;
}

#app {
  font-family: "Inter", sans-serif;
}

@include md-register-theme(
  "default",
  (
    primary: #9a2a28,
    accent: #404040,
    tertiary: #404040,
  )
);

@import "~vue-material/dist/theme/all"; // Apply the theme
#main-body {
  padding-top: 66px;
  padding-bottom: 100px;
}

.with-bg {
  background-image: url("./assets/background.png");
}

.margin-area {
  padding: 20px;
}

.md-layout {
  width: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.3s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  animation-delay: 0.5s;
  transition-duration: 0.5s;
  transition-property: height, opacity, transform;
  transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
  overflow: hidden;
}
.slide-left-enter,
.slide-right-leave-active {
  opacity: 0;
  transform: translate(2em, 0);
}
.slide-left-leave-active,
.slide-right-enter {
  opacity: 0;
  transform: translate(-2em, 0);
}
.zoom-enter-active,
.zoom-leave-active {
  animation-duration: 0.3s;
  animation-fill-mode: both;
  animation-name: zoom;
}
.zoom-leave-active {
  animation-direction: reverse;
}

@keyframes zoom {
  from {
    opacity: 0;
    transform: scale3d(0.3, 0.3, 0.3);
  }
  100% {
    opacity: 1;
  }
}
.margin-twenty {
  margin-top: 10px;
  margin-bottom: 10px;
}

.fixed-menu {
  position: fixed !important;
  top: 0 !important;
  width: 100%;
  margin-bottom: 100px;
  z-index: 3000;
}
</style>




// WEBPACK FOOTER //
// src/App.vue